import { BookOpen, Disc3, PlayCircle, ScrollText, Compass, Zap, Boxes, FileText, Lock } from "lucide-react";
import { BACKEND_URL } from "../lib/api";

// Premium 3D "held object" treatment so every digital asset feels tangible.
// Pass either a real mockup `image` (the fixed gift/codes/disc renders) OR a
// `variant` to render a CSS-built object themed by type, with the title on it.

const ASSET = `${BACKEND_URL}/api/static/assets`;
export const MOCKUPS = {
  gift: `${ASSET}/gift-cover.png`,
  codes: `${ASSET}/codes-cover.png`,
  transmission: `${ASSET}/transmission-disc.png`,
  seal: `${ASSET}/drop-seal.png`,
};

const VARIANTS = {
  book: { Icon: BookOpen, tag: "READ", shape: "book" },
  codex: { Icon: ScrollText, tag: "CODEX", shape: "book" },
  disc: { Icon: Disc3, tag: "LISTEN", shape: "disc" },
  video: { Icon: PlayCircle, tag: "WATCH", shape: "book" },
  flow: { Icon: Boxes, tag: "EXPERIENCE", shape: "book" },
  quickwin: { Icon: Zap, tag: "QUICK WIN", shape: "book" },
  diagnostic: { Icon: Compass, tag: "DIAGNOSTIC", shape: "book" },
  teaching: { Icon: FileText, tag: "TEACHING", shape: "book" },
};

// Map a drop record to a tangible variant + label.
export function dropVariant(d) {
  if (!d) return "teaching";
  if (d.flow_embed) return "flow";
  if (d.youtube_url) return "video";
  if (d.quick_win) return "quickwin";
  return "teaching";
}

export function TangibleAsset({
  variant = "book", title = "", label, image, locked = false,
  className = "", width = "w-28", onClick,
}) {
  const v = VARIANTS[variant] || VARIANTS.book;
  const tag = label || v.tag;
  const Icon = v.Icon;
  const interactive = !!onClick;

  // Real photographic mockup (gift / codes / disc) — float it with a shadow.
  if (image) {
    return (
      <div
        data-testid="tangible-asset"
        onClick={onClick}
        className={`group relative ${width} ${interactive ? "cursor-pointer" : ""} ${className}`}
        style={{ perspective: "900px" }}
      >
        <img
          src={image}
          alt={title}
          className="w-full h-auto object-contain transition-transform duration-500 ease-out group-hover:-translate-y-1.5 group-hover:scale-[1.03]"
          style={{ filter: "drop-shadow(0 22px 30px rgba(0,0,0,0.65)) drop-shadow(0 4px 8px rgba(212,175,55,0.15))" }}
        />
        {locked && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Lock className="w-6 h-6 text-gold/90" />
          </div>
        )}
      </div>
    );
  }

  // CSS-built object (for dynamic drops). Disc = round medallion; book = cover w/ spine + pages.
  const isDisc = v.shape === "disc";
  return (
    <div
      data-testid="tangible-asset"
      onClick={onClick}
      className={`group relative ${width} ${interactive ? "cursor-pointer" : ""} ${className}`}
      style={{ perspective: "900px" }}
    >
      <div
        className="relative w-full transition-transform duration-500 ease-out group-hover:[transform:rotateY(-14deg)_translateY(-4px)]"
        style={{ transformStyle: "preserve-3d", transform: "rotateY(-7deg)" }}
      >
        {/* page-edge stack (book only) */}
        {!isDisc && (
          <div className="absolute right-0 top-1 bottom-1 w-1.5 bg-gradient-to-b from-cream/80 via-cream/60 to-cream/80"
            style={{ transform: "translateX(2px) translateZ(-2px)" }} />
        )}
        <div
          className={`relative bg-void border border-gold/60 ${isDisc ? "rounded-full aspect-square" : "aspect-[3/4]"} overflow-hidden`}
          style={{ boxShadow: "0 20px 28px rgba(0,0,0,0.6), inset 0 0 0 3px rgba(10,10,10,1), inset 0 0 0 4px rgba(212,175,55,0.35)" }}
        >
          {/* spine (book only) */}
          {!isDisc && <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-r from-black to-gold/20" />}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2 py-3">
            <Icon className="w-6 h-6 text-gold mb-2.5" strokeWidth={1.4} />
            <div className="font-mono text-[7px] tracking-[0.2em] text-gold/80 uppercase mb-1.5">{tag}</div>
            <div className="font-display text-cream leading-tight text-[11px] line-clamp-3">{title}</div>
          </div>
          {locked && (
            <div className="absolute inset-0 bg-void/55 flex items-center justify-center backdrop-blur-[1px]">
              <Lock className="w-6 h-6 text-gold" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TangibleAsset;
